import React, { useState, useMemo, useEffect } from 'react';
import { Plus } from 'lucide-react';
import Button from '../../components/ui/Button';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import DealsFilters from './components/DealsFilters';
import DealsTable from './components/DealsTable';
import DealDrawer from './components/DealDrawer';
import TablePagination from './components/TablePagination';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import SampleDataToggle from '../../components/ui/SampleDataToggle';
import { sampleDataService } from '../../services/sampleDataService';
import { dealService } from '../../services/dealService';

import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeEntity } from '../../hooks/useRealtimeEntity';

export default function DealsPage() {
  const { profile } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [filters, setFilters] = useState({
    search: '',
    stage: 'all',
    minValue: '',
    maxValue: '',
    closeDateFrom: '',
    closeDateTo: ''
  });
  const [showSampleData, setShowSampleData] = useState(false);
  const [sampleDeals, setSampleDeals] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  // Read initial state from localStorage
  const getInitialSampleDataState = () => {
    try {
      const stored = localStorage?.getItem(`crm_sample_data_enabled_${profile?.tenant_id}`);
      return stored === 'true';
    } catch {
      return false;
    }
  };

  // Real-time deals synchronization
  const transformDeal = (dbDeal) => ({
    id: dbDeal?.id,
    tenantId: dbDeal?.tenant_id,
    title: dbDeal?.title,
    value: dbDeal?.value,
    stage: dbDeal?.stage,
    probability: dbDeal?.probability,
    expectedCloseDate: dbDeal?.expected_close_date,
    assignedTo: dbDeal?.assigned_to,
    tags: dbDeal?.tags,
    createdAt: dbDeal?.created_at,
    updatedAt: dbDeal?.updated_at
  });

  const { data: realtimeDeals, setData: setRealtimeDeals, isConnected } = useRealtimeEntity(
    'deals',
    deals,
    transformDeal
  );

  // Sync real-time data with local state
  useEffect(() => {
    if (isConnected) {
      setDeals(realtimeDeals);
    }
  }, [realtimeDeals, isConnected]);

  useEffect(() => {
    loadDeals();
  }, [profile?.tenant_id]);

  // Load sample data if toggle is already active on mount
  useEffect(() => {
    if (showSampleData && profile?.tenant_id) {
      loadSampleData();
    }
  }, [profile?.tenant_id]);

  const loadDeals = async () => {
    try {
      setLoading(true);
      // Real-time hook will populate deals automatically
      setDeals([]);
    } catch (error) {
      console.error('Error loading deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSampleData = async () => {
    try {
      const data = await sampleDataService?.getSampleDataByEntity('deals', profile?.tenant_id);
      setSampleDeals(data || []);
    } catch (err) {
      console.error('Error loading sample data:', err);
    }
  };

  // Filter and sort deals
  const filteredAndSortedDeals = useMemo(() => {
    // Combine real and sample data if sample data is active
    const allDeals = showSampleData ? [...deals, ...sampleDeals] : deals;

    let filtered = allDeals?.filter(deal => {
      const matchesSearch = !filters?.search || 
        deal?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        deal?.account?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      
      const matchesStage = !filters?.stage || deal?.stage === filters?.stage;
      const matchesOwner = !filters?.owner || deal?.owner === filters?.owner;
      
      const matchesMinValue = !filters?.minValue || deal?.value >= parseInt(filters?.minValue);
      const matchesMaxValue = !filters?.maxValue || deal?.value <= parseInt(filters?.maxValue);
      
      const matchesCloseDateFrom = !filters?.closeDateFrom || 
        new Date(deal.closeDate) >= new Date(filters.closeDateFrom);
      const matchesCloseDateTo = !filters?.closeDateTo || 
        new Date(deal.closeDate) <= new Date(filters.closeDateTo);

      return matchesSearch && matchesStage && matchesOwner && 
             matchesMinValue && matchesMaxValue && 
             matchesCloseDateFrom && matchesCloseDateTo;
    });

    // Sort deals
    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];

        if (sortConfig?.key === 'value') {
          aValue = parseInt(aValue);
          bValue = parseInt(bValue);
        } else if (sortConfig?.key === 'closeDate') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (typeof aValue === 'string') {
          aValue = aValue?.toLowerCase();
          bValue = bValue?.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [filters, sortConfig]);

  const totalPages = Math.ceil(filteredAndSortedDeals?.length / itemsPerPage);

  const handleAddDeal = () => {
    setSelectedDeal(null);
    setIsDrawerOpen(true);
  };

  const handleSaveDeal = async (dealData) => {
    try {
      if (selectedDeal?.id) {
        await dealService?.update(selectedDeal?.id, dealData);
      } else {
        await dealService?.create(dealData);
      }
      await loadDeals();
      setIsDrawerOpen(false);
    } catch (error) {
      console.error('Error saving deal:', error);
      alert('Failed to save deal: ' + error?.message);
    }
  };

  const handleMenuToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  const handleDealClick = (deal) => {
    setSelectedDeal(deal);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedDeal(null);
  };

  const handleSelectDeal = (dealId, isSelected) => {
    if (isSelected) {
      setSelectedDeals([...selectedDeals, dealId]);
    } else {
      setSelectedDeals(selectedDeals?.filter(id => id !== dealId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const currentPageDeals = filteredAndSortedDeals?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)?.map(deal => deal?.id);
      setSelectedDeals([...new Set([...selectedDeals, ...currentPageDeals])]);
    } else {
      const currentPageDeals = filteredAndSortedDeals?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)?.map(deal => deal?.id);
      setSelectedDeals(selectedDeals?.filter(id => !currentPageDeals?.includes(id)));
    }
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig?.key === key && prevConfig?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      stage: '',
      owner: '',
      minValue: '',
      maxValue: '',
      closeDateFrom: '',
      closeDateTo: ''
    });
    setCurrentPage(1);
  };

  const handleBulkAction = (action) => {
    console.log(`Bulk action ${action} for deals:`, selectedDeals);
    // Implement bulk actions here
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSampleDataToggle = async (isActive) => {
    try {
      setShowSampleData(isActive);
      if (isActive) {
        await loadSampleData();
      } else {
        setSampleDeals([]);
      }
    } catch (err) {
      console.error('Error toggling sample data:', err);
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  return (
    <div className="flex h-screen bg-gray-50">
      <ComponentIdBadge 
        id="pg-deals-main" 
        name="Deals Main Page"
        description="Deal management page with pipeline tracking and revenue forecasting"
        position="top-right"
      />
      <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onToggleSidebar={handleMenuToggle} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6 relative">
              <div>
                <ComponentIdBadge 
                  id="hdr-deals-title" 
                  name="Deals Page Header"
                  description="Page title and deal statistics"
                  position="inline"
                  showLabel={true}
                />
                <h1 className="text-3xl font-bold text-gray-900">Deals</h1>
                <p className="text-gray-600">Track and manage sales opportunities</p>
              </div>
              <div className="flex items-center gap-3">
                <SampleDataToggle
                  entityType="deals"
                  onToggle={handleSampleDataToggle}
                  isActive={showSampleData}
                />
                <div className="relative">
                  <ComponentIdBadge 
                    id="btn-deal-add" 
                    name="Add New Deal Button"
                    description="Opens drawer to create new deal"
                  />
                  <Button
                    onClick={handleAddDeal}
                    className="flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Deal
                  </Button>
                </div>
              </div>
            </div>

            <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <ComponentIdBadge 
                id="cmp-deals-filters" 
                name="Deal Filters Component"
                description="Search and filter controls for deals"
              />
              <DealsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClearFilters={handleClearFilters}
                dealCount={filteredAndSortedDeals?.length || 0}
                onBulkAction={handleBulkAction}
                selectedCount={selectedDeals?.length || 0}
              />
            </div>

            <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <ComponentIdBadge 
                id="tbl-deals-main" 
                name="Deals Data Table"
                description="Main table displaying all deal records with stages and values"
              />
              <DealsTable
                deals={filteredAndSortedDeals?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)}
                selectedDeals={selectedDeals}
                onSelectDeal={handleSelectDeal}
                onSelectAll={handleSelectAll}
                onDealClick={handleDealClick}
                sortConfig={sortConfig}
                onSort={handleSort}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
              />
            </div>

            <div className="relative">
              <ComponentIdBadge 
                id="cmp-pagination-deals" 
                name="Deals Pagination"
                description="Page navigation controls for deals list"
                position="inline"
              />
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredAndSortedDeals?.length || 0}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </main>
      </div>

      {isDrawerOpen && (
        <DealDrawer
          deal={selectedDeal}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onSave={handleSaveDeal}
        />
      )}
    </div>
  );
}